// The double XP badge for every game's main menu (ACT HQ t-1413; Theo, 29 Oct 2026, masta controlla's chat: "put a
// small thing saying double EXP weekend with a countdown to when it ends on the different games' main menus. It can
// just be like a subtle thing"). A small line, "2× XP WEEKEND · ENDS IN 3D 14H", shown only while a window runs. It
// reads the window from the database (pass_xp_window, pass_p19), so new dates need no release; it never takes a click
// and can't be reached by pad. Its accent is the page's: `--act-xpw-accent` on any ancestor, else `--accent`.
//
// Import-light on purpose (DOM and the Supabase client only, its own styles), like the server notice:
//   import { mountXpWindowBadge } from "@act/client-core/xpWindow";
//   mountXpWindowBadge(menuTitleRow);
import { supabase } from "../network/supabaseClient";

/** A window as pass_xp_window returns it. */
export type XpWindow = { multiplier: number; startsAt: number; endsAt: number };

const POLL_MS = 10 * 60_000;
const STYLE_ID = "act-xpw-style";
const STYLES = `
.act-xpw {
  --act-xpw-a: var(--act-xpw-accent, var(--accent, #e8c15a));
  display: inline-flex; align-items: center; gap: 10px; box-sizing: border-box;
  padding: 4px 12px 4px 4px; border: 1px solid color-mix(in srgb, var(--act-xpw-a) 55%, transparent);
  background: rgba(0, 0, 0, 0.42); color: rgba(255, 255, 255, 0.92);
  font: 600 14px/1 Saira, "Helvetica Neue", Arial, sans-serif; letter-spacing: 0.14em; text-transform: uppercase;
  white-space: nowrap; pointer-events: none; user-select: none; -webkit-user-select: none;
}
.act-xpw[hidden] { display: none; }
.act-xpw__x { padding: 5px 8px 4px; background: var(--act-xpw-a); color: #0b0b0d; font-weight: 800; letter-spacing: 0.08em; }
.act-xpw__t { color: rgba(255, 255, 255, 0.62); font-variant-numeric: tabular-nums; }
@media (max-width: 560px) { .act-xpw { font-size: 12.5px; gap: 8px; } }
`;

/** "3D 14H", "14H 05M" or "5M 09S": the two biggest units left. */
export function formatXpRemaining(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(s / 86_400), h = Math.floor((s % 86_400) / 3600), m = Math.floor((s % 3600) / 60);
  const pad = (n: number) => String(n).padStart(2, "0");
  if (d > 0) return `${d}D ${pad(h)}H`;
  if (h > 0) return `${h}H ${pad(m)}M`;
  return `${m}M ${pad(s % 60)}S`;
}

/** "2×", "1.5×". */
export function formatXpMultiplier(m: number): string {
  return `${Number(m.toFixed(2))}×`;
}

/** What the badge says at `now`, or null while no window runs. */
export function xpWindowText(win: XpWindow | null, now: number): { multiplier: string; rest: string } | null {
  if (!win || win.multiplier <= 1 || now < win.startsAt || now >= win.endsAt) return null;
  return { multiplier: `${formatXpMultiplier(win.multiplier)} XP`, rest: `WEEKEND · ENDS IN ${formatXpRemaining(win.endsAt - now)}` };
}

/** The window running now or next, or null (none set, or the database out of reach). */
export async function fetchXpWindow(): Promise<XpWindow | null> {
  if (!supabase) return null;
  try {
    const { data, error } = await supabase.rpc("pass_xp_window");
    const row = !error && Array.isArray(data) ? data[0] : null;
    if (!row) return null;
    const win = { multiplier: Number(row.multiplier), startsAt: Date.parse(row.starts_at), endsAt: Date.parse(row.ends_at) };
    return Number.isFinite(win.multiplier) && Number.isFinite(win.startsAt) && Number.isFinite(win.endsAt) ? win : null;
  } catch {
    return null;
  }
}

/**
 * Puts the badge in `host` (appended, or before `options.before`). It stays hidden while no window runs, shows itself
 * when one starts and counts down each second while the page is visible. Returns a function that removes it.
 */
export function mountXpWindowBadge(
  host: HTMLElement,
  options: { before?: Node | null; className?: string; fetch?: () => Promise<XpWindow | null>; now?: () => number } = {},
): () => void {
  if (!document.getElementById(STYLE_ID)) {
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = STYLES;
    document.head.appendChild(style);
  }
  const now = options.now ?? Date.now;
  const read = options.fetch ?? fetchXpWindow;
  const el = document.createElement("div");
  el.className = `act-xpw${options.className ? ` ${options.className}` : ""}`;
  el.hidden = true;
  el.innerHTML = '<span class="act-xpw__x"></span><span class="act-xpw__t"></span>';
  const x = el.firstElementChild as HTMLElement;
  const t = el.lastElementChild as HTMLElement;
  host.insertBefore(el, options.before ?? null);

  let win: XpWindow | null = null;
  let gone = false;
  const render = (): void => {
    const text = xpWindowText(win, now());
    el.hidden = !text;
    if (!text) return;
    if (x.textContent !== text.multiplier) x.textContent = text.multiplier;
    if (t.textContent !== text.rest) t.textContent = text.rest;
  };
  const poll = async (): Promise<void> => {
    const next = await read();
    if (gone) return;
    win = next;
    render();
  };
  const onVisible = (): void => {
    if (document.visibilityState === "visible") void poll();
  };
  void poll();
  const poller = window.setInterval(() => void poll(), POLL_MS);
  const ticker = window.setInterval(() => {
    if (document.visibilityState === "visible") render();
  }, 1000);
  document.addEventListener("visibilitychange", onVisible);
  return () => {
    gone = true;
    window.clearInterval(poller);
    window.clearInterval(ticker);
    document.removeEventListener("visibilitychange", onVisible);
    el.remove();
  };
}
